/**
 * The `/impersonate` gate: the only place in the app that may write a session
 * token it did not mint itself.
 *
 * It renders INSTEAD of the app (main.tsx gives it its own root) for two
 * reasons. The app must not mount while a handoff is pending — no auth
 * provider may observe the pre-handoff session, which was the original reason
 * the handoff ran before React — and this screen must not be reachable from
 * inside a signed-in app shell, where it would read as a normal page rather
 * than the identity switch it is.
 *
 * The flow, in order:
 *
 *  1. Resolve the token against the worker (../impersonation-handoff). Nothing
 *     is stored yet — the claim lives in a prop for the length of this screen.
 *  2. REFUSE anything that is not a genuine admin impersonation session, and
 *     say so. A refusal is not a dead end to hide: if a stranger's link brought
 *     someone here, the refusal screen is the only thing that tells them.
 *  3. Otherwise show WHO — the target account and the admin who asked — and
 *     wait for a click. `window.confirm` would not do: it cannot render the
 *     two identities legibly, and a native dialog on an unexpected page is
 *     exactly the prompt people dismiss without reading.
 *  4. Only then clear this tab's existing credentials and commit the token,
 *     then hard-navigate to `/` so every module (JWT cache included) restarts
 *     against the new identity.
 *
 * Either way out clears first: cancelling or being refused drops whatever
 * Bearer this tab was holding, so a link that failed the check cannot leave a
 * half-applied identity behind. It costs a signed-out visitor nothing — they
 * have no Bearer — and the first-party cookie is untouched in every case.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageLoader } from '@/components/loading/page-loader';

import { resetLocalCredentials } from '../client';
import {
  type ImpersonationTarget,
  resolveImpersonationSession,
  SESSION_TOKEN_KEY,
} from '../impersonation-handoff';

type Phase =
  | { kind: 'checking' }
  | { kind: 'confirm'; target: ImpersonationTarget }
  | { kind: 'refused' };

/**
 * The real navigation, injectable ONLY so this flow is testable — the same
 * trick ./impersonation-banner and ../client use. jsdom's `window.location` is
 * [LegacyUnforgeable], so a test cannot observe a real navigation.
 *
 * `replace`, not `assign`: the handoff URL must not sit in history where Back
 * would re-run it.
 */
function navigateHome(): void {
  window.location.replace('/');
}

export function ImpersonateConfirm({
  token,
  navigate = navigateHome,
}: {
  /** The unverified claim from the URL fragment; `null` for a malformed link. */
  token: string | null;
  navigate?: () => void;
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>(
    token === null ? { kind: 'refused' } : { kind: 'checking' },
  );
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    if (token === null) return;
    let live = true;
    void resolveImpersonationSession(token).then((target) => {
      if (!live) return;
      setPhase(target ? { kind: 'confirm', target } : { kind: 'refused' });
    });
    return () => {
      live = false;
    };
  }, [token]);

  // A refused link leaves nothing behind — including whatever this tab was
  // already carrying. Runs on entering the state, not on the click, so it holds
  // even if the operator just closes the tab.
  useEffect(() => {
    if (phase.kind === 'refused') resetLocalCredentials();
  }, [phase.kind]);

  function cancel() {
    resetLocalCredentials();
    navigate();
  }

  function commit() {
    // Unreachable — `checking` is the only phase a null token can reach — but
    // the guard is what makes the write below provably token-non-null.
    if (token === null) return;
    setCommitting(true);
    // Order matters: the previous Bearer and the cached JWT it minted go FIRST,
    // so no fragment of the outgoing identity can outlive the swap even if the
    // navigation below is slow or blocked.
    resetLocalCredentials();
    localStorage.setItem(SESSION_TOKEN_KEY, token);
    navigate();
  }

  // Resolving the link: the page loader on the page's ground, not an empty
  // card — nothing can be decided (or shown) until the worker answers.
  if (phase.kind === 'checking') {
    return <PageLoader variant="screen" label={t('pageLoader.checkingImpersonation')} />;
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-2xl border bg-background p-8 shadow-sm">
        {phase.kind === 'refused' && (
          <>
            <h1 className="text-lg font-semibold text-destructive">
              {t('auth.impersonation.confirm.refusedTitle')}
            </h1>
            <p role="alert" className="mt-2 text-sm text-muted-foreground">
              {t('auth.impersonation.confirm.refusedBody')}
            </p>
            <button
              type="button"
              onClick={navigate}
              className="mt-6 w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
              {t('auth.impersonation.confirm.home')}
            </button>
          </>
        )}

        {phase.kind === 'confirm' && (
          <>
            <h1 className="text-lg font-semibold">
              {t('auth.impersonation.confirm.title')}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('auth.impersonation.confirm.intro')}
            </p>
            <dl className="mt-6 flex flex-col gap-3 rounded-lg border p-4 text-sm">
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  {t('auth.impersonation.confirm.targetLabel')}
                </dt>
                <dd className="font-medium">
                  {phase.target.name
                    ? t('auth.impersonation.confirm.targetNamed', {
                        name: phase.target.name,
                        email: phase.target.email,
                      })
                    : phase.target.email}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  {t('auth.impersonation.confirm.adminLabel')}
                </dt>
                <dd className="font-medium">{phase.target.impersonatedBy}</dd>
              </div>
            </dl>
            <p className="mt-4 text-sm font-medium text-amber-700 dark:text-amber-400">
              {t('auth.impersonation.confirm.warning')}
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <button
                type="button"
                disabled={committing}
                onClick={commit}
                className="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50">
                {t('auth.impersonation.confirm.action')}
              </button>
              <button
                type="button"
                disabled={committing}
                onClick={cancel}
                className="w-full rounded-md border border-secondary-200 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-50">
                {t('auth.impersonation.confirm.cancel')}
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
